/**
 * API 客户端 — 封装 fetch + JWT + 分页 + 错误处理 + 离线队列
 *
 * VITE_API_BASE_URL — override for chrome-extension (absolute URL needed)
 * Defaults to '/api/v1' for dev server proxy.
 */

import { enqueueIfOffline, initOfflineQueue } from './offline-queue.js';
import { getPerfTraceHeaders, recordPerfApiTiming } from './perf-trace';
import { isBooxPlatformHost, isTenantAppHost } from './tenant-host.js';

function resolveDefaultBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // Tenant storefront/app hosts are static Cloudflare Pages surfaces.
    // Their API must always go to the shared platform backend.
    if (isTenantAppHost(host) || isBooxPlatformHost(host)) {
      return 'https://hub.boox.app/api/v1';
    }
  }
  return '/api/v1';
}

export const BASE: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE_URL) ||
  resolveDefaultBaseUrl();

function resolveWarehouseApiSurfacePath(path: string): string {
  if (typeof window === 'undefined') return path;
  if (!(path === '/wms' || path.startsWith('/wms/'))) return path;

  const browserPath = window.location.pathname;
  if (browserPath === '/wp' || browserPath.startsWith('/wp/') || browserPath === '/pwa/wp' || browserPath.startsWith('/pwa/wp/')) {
    return path.replace(/^\/wms\b/, '/wp');
  }

  return path;
}

// ─── Types ─────────────────────────────────────────

export interface ApiEnvelope<T> {
  ok: true;
  data: T;
}

export interface ApiError {
  ok: false;
  error: { code: string; message: string; details?: unknown };
}

export interface PaginatedData<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export type ApiResult<T> = ApiEnvelope<T> | ApiError;
export type ApiQueryValue = string | number | boolean | null | undefined;
export type ApiQueryParams = Record<string, ApiQueryValue | ApiQueryValue[]>;

export interface StateEntryRecord {
  id: string;
  tenantId: string;
  stateType: string;
  scopeType: string;
  scopeKey: string;
  key: string;
  valueJson: unknown;
  sourceType: string;
  sourceRef: string | null;
  confidence: number | null;
  status: string;
  sensitivity: string;
  retentionClass: string;
  evidenceCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface StateSnapshotRecord {
  id: string;
  tenant_id: string;
  snapshot_ref: string;
  manifest: Record<string, unknown>;
  payload: unknown;
  payload_hash: string | null;
  created_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface StateExportJobRecord {
  id: string;
  tenant_id: string;
  status: string;
  format: string;
  manifest: Record<string, unknown>;
  storage_key: string | null;
  error_message: string | null;
  created_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface StateEvidenceRecord {
  id: string;
  tenantId: string;
  stateEntryId: string;
  evidenceType: string;
  refTable: string | null;
  refId: string | null;
  excerpt: string | null;
  traceId: string | null;
  createdAt: string;
}

export interface StateEdgeRecord {
  id: string;
  tenantId: string;
  fromStateEntryId: string;
  edgeType: string;
  toStateEntryId: string | null;
  toScopeType: string | null;
  toScopeKey: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface StateSnapshotReplay {
  snapshotRef: string;
  manifest: Record<string, unknown>;
  payload: unknown;
  payloadHash: string | null;
  createdAt: string;
}

// ─── Token management ──────────────────────────────

function readTokenFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return (
    localStorage.getItem('boox_token')
    || localStorage.getItem('access_token')
    || sessionStorage.getItem('boox_token')
    || null
  );
}

let token: string | null = typeof localStorage !== 'undefined' ? readTokenFromStorage() : null;

export function setToken(t: string | null) {
  token = t;
  if (typeof localStorage === 'undefined') return;
  if (t) {
    localStorage.setItem('boox_token', t);
    sessionStorage.setItem('boox_token', t);
  } else {
    localStorage.removeItem('boox_token');
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('boox_token');
  }
}

export function getToken(): string | null {
  token = readTokenFromStorage();
  return token;
}

// ─── Fetch wrapper ─────────────────────────────────
// fix: only set Content-Type when body is present

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const resolvedPath = resolveWarehouseApiSurfacePath(path);
  const authToken = getToken();
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const method = init?.method ?? 'GET';
  const headers: Record<string, string> = {
    ...((init?.body && !isFormData) ? { 'Content-Type': 'application/json' } : {}),
    ...getPerfTraceHeaders(resolvedPath),
    ...(init?.headers as Record<string, string>),
  };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

  let res: Response;
  const startedAtMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    const requestUrl = (() => {
      if (/^https?:\/\//i.test(resolvedPath)) return resolvedPath;
      if (resolvedPath.startsWith('/api/')) return resolvedPath;
      return `${BASE}${resolvedPath}`;
    })();
    res = await fetch(requestUrl, {
      ...init,
      headers,
    });
  } catch (fetchErr) {
    const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAtMs;
    recordPerfApiTiming({
      method,
      path: resolvedPath,
      status: 0,
      durationMs: Math.round(durationMs),
      error: fetchErr instanceof Error ? fetchErr.message : 'fetch_failed',
      startedAtMs,
    });
    // Network failure — try to queue mutation for offline replay
    const bodyStr = typeof init?.body === 'string' ? init.body : null;
    const queued = await enqueueIfOffline(method, resolvedPath, bodyStr, fetchErr);
    if (queued) {
      throw new ApiRequestError('OFFLINE_QUEUED', '操作已保存，将在恢复网络后自动提交', 0);
    }
    throw fetchErr;
  }

  // Guard against empty body (204 No Content or network-level empty response)
  const text = await res.text();
  const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAtMs;
  recordPerfApiTiming({
    method,
    path: resolvedPath,
    status: res.status,
    durationMs: Math.round(durationMs),
    responseBytes: text.length,
    serverTiming: res.headers.get('server-timing'),
    startedAtMs,
  });
  if (!text) {
    if (res.ok) return undefined as T;
    throw new ApiRequestError('EMPTY_RESPONSE', `Server returned ${res.status} with empty body`, res.status);
  }

  let body: ApiResult<T>;
  try {
    body = JSON.parse(text) as ApiResult<T>;
  } catch {
    throw new ApiRequestError('INVALID_JSON', `Server returned non-JSON response (${res.status})`, res.status);
  }

  if (!body.ok) {
    const err = body as ApiError;
    throw new ApiRequestError(err.error.code, err.error.message, res.status, err.error.details);
  }

  return (body as ApiEnvelope<T>).data;
}

export async function apiFetchBlob(
  path: string,
  init?: RequestInit,
): Promise<{ blob: Blob; contentType: string | null; headers: Headers }> {
  const resolvedPath = resolveWarehouseApiSurfacePath(path);
  const authToken = getToken();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string>),
  };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  const res = await fetch(`${BASE}${resolvedPath}`, {
    ...init,
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    try {
      const body = text ? JSON.parse(text) as ApiError : null;
      if (body && !body.ok) {
        throw new ApiRequestError(body.error.code, body.error.message, res.status, body.error.details);
      }
    } catch (error) {
      if (error instanceof ApiRequestError) throw error;
    }
    throw new ApiRequestError('BLOB_FETCH_FAILED', `Failed to fetch blob (${res.status})`, res.status);
  }
  return {
    blob: await res.blob(),
    contentType: res.headers.get('content-type'),
    headers: res.headers,
  };
}

export class ApiRequestError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

// ─── Shorthand helpers ─────────────────────────────

function withQuery(path: string, query?: ApiQueryParams): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === undefined || item === null || item === '') continue;
      params.append(key, String(item));
    }
  }
  const qs = params.toString();
  if (!qs) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
}

const inFlightGetRequests = new Map<string, Promise<unknown>>();

function dedupedGet<T>(path: string): Promise<T> {
  const authToken = getToken();
  const key = `${authToken ?? ''}:${path}`;
  const existing = inFlightGetRequests.get(key);
  if (existing) return existing as Promise<T>;

  const request = apiFetch<T>(path);
  inFlightGetRequests.set(key, request);
  request.finally(() => {
    if (inFlightGetRequests.get(key) === request) {
      inFlightGetRequests.delete(key);
    }
  });
  return request;
}

export const api = {
  get: <T>(path: string, query?: ApiQueryParams) => dedupedGet<T>(withQuery(path, query)),
  getBlob: (path: string) => apiFetchBlob(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: typeof FormData !== 'undefined' && body instanceof FormData ? body : JSON.stringify(body),
    }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, {
      method: 'PUT',
      body: typeof FormData !== 'undefined' && body instanceof FormData ? body : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, {
      method: 'PATCH',
      body: typeof FormData !== 'undefined' && body instanceof FormData ? body : JSON.stringify(body),
    }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

export const stateApi = {
  listState: (query = '') =>
    api.get<{ items: StateEntryRecord[]; total: number; page: number; limit: number; view: string }>(`/state${query ? `?${query}` : ''}`),
  getStateEntry: (id: string) =>
    api.get<StateEntryRecord>(`/state/${encodeURIComponent(id)}`),
  listEvidence: (id: string, query = '') =>
    api.get<{ items: StateEvidenceRecord[]; page: number; limit: number }>(
      `/state/${encodeURIComponent(id)}/evidence${query ? `?${query}` : ''}`,
    ),
  listEdges: (id: string, query = '') =>
    api.get<{ items: StateEdgeRecord[]; page: number; limit: number }>(
      `/state/${encodeURIComponent(id)}/edges${query ? `?${query}` : ''}`,
    ),
  listSnapshots: (query = '') =>
    api.get<{ items: StateSnapshotRecord[]; total: number; page: number; limit: number }>(`/state/snapshots${query ? `?${query}` : ''}`),
  createSnapshot: (body: {
    snapshotRef?: string;
    reason?: string;
    manifest?: Record<string, unknown>;
    filter?: Record<string, unknown>;
  }) => api.post<StateSnapshotRecord>('/state/snapshots', body),
  getSnapshot: (snapshotRef: string) =>
    api.get<StateSnapshotRecord>(`/state/snapshots/${encodeURIComponent(snapshotRef)}`),
  replaySnapshot: (snapshotRef: string) =>
    api.get<StateSnapshotReplay>(
      `/state/snapshots/${encodeURIComponent(snapshotRef)}/replay`,
    ),
  listExportJobs: (query = '') =>
    api.get<{ items: StateExportJobRecord[]; total: number; page: number; limit: number }>(`/state/export/jobs${query ? `?${query}` : ''}`),
  createExportJob: (body: {
    format?: 'json';
    reason?: string;
    manifest?: Record<string, unknown>;
    filter?: Record<string, unknown>;
  }) => api.post<StateExportJobRecord>('/state/export/jobs', body),
  getExportJob: (id: string) =>
    api.get<StateExportJobRecord>(`/state/export/jobs/${encodeURIComponent(id)}`),
  getExportDownloadUrl: (id: string) =>
    `${BASE}/state/export/jobs/${encodeURIComponent(id)}/download`,
};

// ─── Offline queue initialization ──────────────────

/** Raw fetch with auth headers for queue replay (bypasses apiFetch to avoid re-queueing) */
function rawAuthFetch(path: string, init: RequestInit): Promise<Response> {
  const resolvedPath = resolveWarehouseApiSurfacePath(path);
  const authToken = getToken();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return fetch(`${BASE}${resolvedPath}`, { ...init, headers });
}

// Lazy-init: only in browser environment
if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
  initOfflineQueue(rawAuthFetch).catch(() => { /* IndexedDB unavailable */ });
}
